import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import SearchIcon from "@mui/icons-material/Search";
import LanguageSelector from "@nualang/nualang-ui-components/dist/Forms/LanguageSelector/LanguageSelector";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import StickyBar from "@nualang/nualang-ui-components/dist/Misc/StickyBar";
import config from "../../../config";
import stickyBarBleed from "../stickyBarBleed";

const VISIBILITY_OPTIONS = ["public", "private"];
const SORT_OPTIONS = ["newest", "oldest", "alphabetical"];
const { languages } = config;

const useStyles = makeStyles()((theme) => ({
  box: {
    ...stickyBarBleed(theme),
    marginTop: theme.spacing(-1),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  actions: {
    marginBottom: theme.spacing(1),
  },
}));

export default function ClassroomsToolbar({
  t = (text) => text,
  visibility,
  onVisibilityChange,
  lang,
  onLangChange,
  sort,
  onSortChange,
  search = "",
  onSearchChange,
  onClearFilters,
  clearFiltersDisabled = false,
  showVisibilityFilter = true,
}) {
  const { classes } = useStyles();
  const [searchText, setSearchText] = useState(search);
  const debouncedSearch = useDebounce(searchText, 500);
  // Tracks the last value we told the parent about, so we can tell a `search`
  // prop change caused by our own debounce commit apart from an external
  // reset (e.g. Clear Filters) without relying on a possibly-stale closure.
  const lastReportedSearch = useRef(search);

  useEffect(() => {
    if (search !== lastReportedSearch.current) {
      lastReportedSearch.current = search;
      setSearchText(search);
    }
  }, [search]);

  useEffect(() => {
    if (onSearchChange && debouncedSearch !== lastReportedSearch.current) {
      lastReportedSearch.current = debouncedSearch;
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange]);

  const handleClearFilters = () => {
    setSearchText("");
    lastReportedSearch.current = "";
    onClearFilters();
  };

  const languageMenuOptions = { all: {}, ...languages };

  return (
    <StickyBar className={classes.box}>
      <Box
        className={classes.actions}
        sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}
      >
        <TextField
          id='search'
          name='search'
          data-cy='search'
          label={t("search")}
          placeholder={t("search_classrooms_placeholder") || "Search classrooms"}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          size="small"
          variant="outlined"
          sx={{ flex: "1 1 200px", maxWidth: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {showVisibilityFilter && (
          <TextField
            select
            id='visibility'
            name='visibility'
            data-cy='visibility'
            label={t("visibility")}
            value={visibility}
            onChange={onVisibilityChange}
            size="small"
            variant="outlined"
            sx={{ minWidth: 130 }}
          >
            <MenuItem value='all'>{t("all")}</MenuItem>
            {VISIBILITY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t(option)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <LanguageSelector
          id='lang'
          name='lang'
          label={t("learn")}
          size="small"
          variant="outlined"
          sx={{ minWidth: 150 }}
          t={t}
          languages={languageMenuOptions}
          value={lang}
          onChange={onLangChange}
        />
        <TextField
          select
          id='sort'
          name='sort'
          data-cy='sort'
          label={t("sort")}
          value={sort}
          onChange={onSortChange}
          size="small"
          variant="outlined"
          sx={{ minWidth: 150 }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </TextField>
        {onClearFilters && (
          <Button
            onClick={handleClearFilters}
            disabled={clearFiltersDisabled}
            data-cy='clear-filters'
          >
            {t("clear_filters")}
          </Button>
        )}
      </Box>
    </StickyBar>
  );
}
